import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { useSignalEffect } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import clsx from 'clsx';
import type { MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import useOdinMemo from '../../common/hooks/use-odin-memo';
import useTestTag from '../../common/hooks/use-test-tag';
import type { OdinKey } from '../../common/types/odin-key.type';
import type { JsonData, JsonDataItem } from '../generic-types';
import type { TemplateType } from '../template-outlet/template-outlet.component';
import TemplateOutlet from '../template-outlet/template-outlet.component';
import type { DataGridCellRenderer } from './data-grid-custom-column.interface';
import DataGridDataRowItem from './data-grid-data-row-item.component';
import { findRowIndex, getColumnDefaultWidth } from './data-grid-functions';
import type { DataGridColumnSettings, DataGridRowIndicator } from './data-grid-settings.interface';

export interface DataGridDataRowProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  selectedRows: Signal<JsonData>;
  highlightedRows: Signal<JsonData>;
  idField: ReadonlySignal<DataGridColumnSettings | undefined>;
  rowIndex: number;
  data: JsonDataItem;
  isEven: boolean;
  rowIndicator?: DataGridRowIndicator;
  rowIsClickable?: boolean;
  components?: Record<string, DataGridCellRenderer>;
  rowTemplate?: RowTemplateType;
  allowCellScrollBar?: boolean;
  rowId?: string;
  onMeasured?: (columnId: string, width: number, rowId?: string) => void;
  isSizingActive?: boolean;
}

export interface DataGridDataRowTemplateProps extends DataGridDataRowProps {
  isRowSelected: boolean;
  onRowClicked: (event: MouseEvent<HTMLElement>) => void;
}

export type RowTemplateType = TemplateType<DataGridDataRowTemplateProps>;

export default function DataGridDataRow({
  columns,
  selectedRows,
  highlightedRows,
  idField,
  rowIndex,
  data,
  isEven,
  rowIndicator,
  rowIsClickable,
  components,
  rowTemplate,
  allowCellScrollBar,
  rowId,
  onMeasured,
  isSizingActive,
}: DataGridDataRowProps) {
  const tagCreator = useTestTag('row');
  useSignals();

  const [isRowSelected, setIsRowSelected] = useState(false);
  const [isRowHighlighted, setIsRowHighlighted] = useState(false);

  useSignalEffect(() => {
    setIsRowSelected(findRowIndex(selectedRows.value, data, idField.value) > -1);
  });

  useSignalEffect(() => {
    setIsRowHighlighted(findRowIndex(highlightedRows.value, data, idField.value) > -1);
  });

  const onRowClicked = () => {
    selectedRows.value = [data];
  };

  const onIndicatorClicked = () => {
    onRowClicked();

    if (idField.value && rowIndicator?.onIndicatorClicked) {
      rowIndicator.onIndicatorClicked(data[idField.value.field] as OdinKey);
    }
  };

  const templateProps: DataGridDataRowTemplateProps = {
    columns,
    selectedRows,
    highlightedRows,
    idField,
    rowIndex,
    data,
    isEven,
    rowIsClickable,
    components,
    isRowSelected,
    onRowClicked,
  };

  const defaultTemplate = (props: DataGridDataRowTemplateProps, children?: ReactNode | Array<ReactNode>) => {
    const onRowClick = (event: MouseEvent<HTMLElement>) => {
      if (props.rowIsClickable) {
        props.onRowClicked(event);
      }
    };

    return (
      <div className="flex" onClick={onRowClick}>
        {children}
      </div>
    );
  };

  const template = rowTemplate || defaultTemplate;

  const rowContainerClassNames = clsx('group flex min-w-full text-sm', {
    'cursor-default': !rowIsClickable,
  });

  const rowClassNames = clsx('flex', {
    'bg-gray-5': !isEven && !isRowSelected,
    'bg-mono-1': isEven && !isRowSelected,
    'bg-primary-4': isRowHighlighted && !isRowSelected,
    'bg-primary-3 text-mono-1 hover:text-mono-0': isRowSelected,
    'cursor-pointer hover:bg-primary-4': rowIsClickable,
  });

  // NOTE: if modifying the width 'w-10', please remember to update 'STATUS_INDICATOR_CELL_WIDTH_PX'
  const indicatorClassNames = clsx(
    'flex w-10 shrink-0 items-center justify-center self-stretch bg-mono-1 py-2',
    rowIndicator?.indicatorClassNames
  );

  const indicatorInnerClassNames = clsx({
    'cursor-pointer opacity-0 transition-opacity group-hover:opacity-100': rowIndicator?.showOnHoverOnly === true,
    'opacity-100': isRowSelected,
  });

  const rowData = useOdinMemo<JsonDataItem>(
    () => {
      return {
        ...data,
        rowIndex,
      };
    },
    [data, rowIndex],
    // exclude linked records from deep equals check
    ['_linkedRecordColumnValuesMap']
  );

  return (
    <div className={rowContainerClassNames}>
      {rowIndicator && (
        <div className={indicatorClassNames} onClick={onIndicatorClicked}>
          <div className={indicatorInnerClassNames}>{rowIndicator.indicator(data)}</div>
        </div>
      )}
      <TemplateOutlet props={templateProps} template={template}>
        <div className={rowClassNames} ref={tagCreator(String(rowIndex))}>
          {columns.value
            .filter((column) => column.visible)
            .map((column) => {
              // Always ensure column has a width for visual consistency between header and body
              const effectiveColumn =
                column.width === undefined ? { ...column, width: getColumnDefaultWidth(column) } : column;

              return (
                <DataGridDataRowItem
                  key={`${rowIndex}_${column.id}`}
                  item={rowData}
                  column={effectiveColumn}
                  components={components}
                  allowCellScrollBar={allowCellScrollBar}
                  rowId={rowId}
                  onMeasured={onMeasured}
                  isSizingActive={isSizingActive}
                />
              );
            })}
        </div>
      </TemplateOutlet>
    </div>
  );
}
